import { useState } from 'react';
import { testimonials } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';

const FORM_ADDR = 'https://readdy.ai/api/form/da23rddv7gurs51ef5o0';

const principles = [
  { icon: 'ri-flashlight-fill', title: '抢先更新', desc: '热门连载最新话上线即同步，抢先看优先呈现，让追更快人一步。' },
  { icon: 'ri-refresh-line', title: '进度透明', desc: '同步更新进度与汉化进度全程实时可查，追更心中有数。' },
  { icon: 'ri-check-double-line', title: '更新同步', desc: '版本更新信息多端同步，无论何时打开都不错过任何一话。' },
  { icon: 'ri-heart-3-line', title: '用心追更', desc: '持续优化阅读体验，认真对待每一条反馈，做最懂追更人的平台。' },
];

const values = [
  { no: '01', title: '追更便捷', desc: '更新动态、最新话入口集中呈现，减少搜索与等待。' },
  { no: '02', title: '信息真实', desc: '更新进度与汉化进度如实展示，不夸大、不误导。' },
  { no: '03', title: '内容多元', desc: '覆盖热血、恋爱、奇幻、悬疑等多类型，满足不同口味。' },
  { no: '04', title: '体验友好', desc: '响应式设计，电脑、平板、手机皆可顺畅追更。' },
];

export default function About() {
  useSeo({
    title: '关于我们 - 策驰漫画网，抢先看最新话与同步更新进度',
    description:
      '了解策驰漫画网的品牌定位、发展理念与内容原则。我们致力于让追更不再错过，抢先看最新话、同步更新进度与汉化进度实时可查。',
    keywords: '策驰漫画网,关于我们,追更进度,抢先看最新话,同步更新,汉化进度',
  });

  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('idle');
    setFormError('');
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = fd.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }
    fd.delete('website_alt');

    try {
      const res = await fetch(FORM_ADDR, {
        method: 'POST',
        body: fd,
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = res.ok && parsed?.code === 'OK' && !isSpam;
      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后再试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于策驰漫画网',
        url: 'https://gifcart.github.io/about',
        description: '策驰漫画网的品牌定位、发展理念与内容原则。',
      },
      {
        '@type': 'Organization',
        name: '策驰漫画网',
        description: '抢先看最新话，同步更新进度，汉化进度实时查的漫画追更平台。',
      },
    ],
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-20 pt-28 md:px-6 md:pt-32">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      <div className="mt-6 max-w-3xl">
        <h1 className="font-heading text-2xl leading-tight text-foreground-950 md:text-4xl">关于策驰漫画网</h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600">
          策驰漫画网是一个聚焦漫画追更体验的更新平台，核心围绕三件事：抢先看最新话、同步更新进度、汉化进度实时查。
          我们相信，好的追更应该省心、透明、不等待。
        </p>
      </div>

      <section className="mt-12">
        <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">平台定位</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          我们面向热爱追更的漫画读者，为每一部连载提供清晰的同步更新进度与汉化进度展示。
          热门连载的最新话上线即同步，抢先看内容优先呈现，让读者第一时间掌握更新动态，追更不再错过任何一话。
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {principles.map((p) => (
            <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-xl text-primary-600">
                <i className={p.icon}></i>
              </span>
              <h3 className="mt-4 text-sm font-bold text-foreground-950">{p.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">发展理念</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          我们坚持内容驱动与读者优先，围绕更新同步与追更体验持续打磨，让平台越用越顺手。
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {values.map((v) => (
            <div key={v.no} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="font-heading text-3xl text-accent-500">{v.no}</span>
              <div>
                <h3 className="text-sm font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">读者口碑</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <blockquote className="text-sm leading-relaxed text-foreground-700">“{t.content}”</blockquote>
              <figcaption className="mt-4 flex items-center gap-3">
                <img src={t.avatar} alt={`${t.name} 头像`} title={`${t.name} - 追更用户`} className="h-10 w-10 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                  <p className="text-xs text-foreground-500">{t.role}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
        <div>
          <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">联系我们</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            无论你是想反馈更新问题、建议新作品上架，还是有合作意向，都欢迎通过以下方式或表单与我们联系。
          </p>
          <ul className="mt-6 space-y-4 text-sm text-foreground-700">
            <li className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-lg text-primary-600"><i className="ri-mail-line"></i></span>
              <div>
                <p className="text-xs text-foreground-500">邮箱</p>
                <p className="font-medium">contact@cechi-mh.example</p>
              </div>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-700"><i className="ri-time-line"></i></span>
              <div>
                <p className="text-xs text-foreground-500">在线时间</p>
                <p className="font-medium">每日 09:00 - 22:00</p>
              </div>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700"><i className="ri-customer-service-2-line"></i></span>
              <div>
                <p className="text-xs text-foreground-500">服务范围</p>
                <p className="font-medium">更新问题 · 建议反馈 · 合作洽谈</p>
              </div>
            </li>
          </ul>
        </div>

        <form
          data-readdy-form
          onSubmit={onSubmit}
          className="rounded-lg border border-background-200 bg-background-50 p-5"
        >
          <h3 className="text-base font-bold text-foreground-950">给我们留言</h3>
          <div className="mt-4 space-y-3">
            <input
              name="website_alt"
              type="text"
              autoComplete="off"
              aria-hidden="true"
              readOnly
              tabIndex={-1}
              className="hp-hide"
              placeholder=""
            />
            <div>
              <label htmlFor="c-name" className="mb-1.5 block text-xs font-medium text-foreground-700">称呼</label>
              <input
                id="c-name"
                name="name"
                type="text"
                required
                placeholder="您的称呼"
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm outline-none ring-primary-300 placeholder:text-foreground-400 focus:ring-2"
              />
            </div>
            <div>
              <label htmlFor="c-email" className="mb-1.5 block text-xs font-medium text-foreground-700">邮箱</label>
              <input
                id="c-email"
                name="email"
                type="email"
                required
                placeholder="用于接收回复"
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm outline-none ring-primary-300 placeholder:text-foreground-400 focus:ring-2"
              />
            </div>
            <div>
              <label htmlFor="c-topic" className="mb-1.5 block text-xs font-medium text-foreground-700">主题</label>
              <input
                id="c-topic"
                name="topic"
                type="text"
                required
                placeholder="更新反馈 / 建议 / 合作"
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm outline-none ring-primary-300 placeholder:text-foreground-400 focus:ring-2"
              />
            </div>
            <div>
              <label htmlFor="c-msg" className="mb-1.5 block text-xs font-medium text-foreground-700">留言内容</label>
              <textarea
                id="c-msg"
                name="message"
                rows={4}
                required
                maxLength={500}
                placeholder="请描述您的问题或建议（500 字以内）"
                className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm outline-none ring-primary-300 placeholder:text-foreground-400 focus:ring-2"
              ></textarea>
            </div>
            {status === 'error' && (
              <p className="rounded-md bg-primary-50 px-3 py-2 text-xs text-primary-700">{formError}</p>
            )}
            {status === 'success' && (
              <p className="rounded-md bg-secondary-50 px-3 py-2 text-xs text-secondary-700">
                提交成功，感谢您的留言，我们会尽快回复。
              </p>
            )}
            <button
              type="submit"
              className="w-full whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              提交留言
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}